import {
  FacebookShareButton,
  FacebookIcon,
  TwitterShareButton,
  EmailShareButton,
  EmailIcon,
  CopyToClipboard,
} from "../../atoms";
import React from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { Group } from "@dreambigger/design-system/src";
import InspireAnalytics, {
  InspireAnalyticsHelper,
  InspireAnalyticEvents,
} from "@dreambigger/shared/src/utils/inspire-analytics-helper";
import { useGA, useSegment, useMixpanel } from "@dreambigger/shared/src/hooks";
import { XOutlined } from "@ant-design/icons";

interface ShareBarProps {
  facebook?: boolean;
  twitter?: boolean;
  email?: boolean;
  clipboard?: boolean;
  rsvpUrl?: string;
  buttonClass?: string;
  shareMessageSubject?: string | undefined;
  shareMessageBody?: string | undefined;
  labeledButtons?: boolean;
  rectangularButtons?: boolean;
  liftOnHover?: boolean;
  shareUrl?: string; // Optional custom URL to share (defaults to current page URL)
  onShare?: (shareType: string) => void;
  analyticsProperties?: Record<string, any>;
}

// DEFAULT SHARE MESSAGE BODY IF NOT PASSED IN
// Dev Note: Preserve line and tab spacing between these lines because of template literal ---
const defaultShareBody = `Hello! I wanted to share this link with you because I thought you might find it interesting. 
  
Here is the link if you want to check it out:`;

const ShareBar = ({
  facebook = true,
  twitter = true,
  email = true,
  clipboard = true,
  rsvpUrl,
  shareMessageSubject = `Check this out!`,
  shareMessageBody = defaultShareBody,
  buttonClass = "h-7 w-7",
  labeledButtons,
  rectangularButtons,
  liftOnHover,
  shareUrl: customShareUrl,
  onShare,
  analyticsProperties,
}: ShareBarProps) => {
  // Set up analytics
  const mixpanel = useMixpanel();
  const router = useRouter();
  const segment = useSegment();
  const googleAnalytics = useGA();

  // Use custom URL if provided, otherwise default to current page URL
  const shareUrl =
    customShareUrl ||
    (process?.browser ? window.location.href.split("?")[0] : "");

  const handleClick = (shareType: string) => {
    // Handle the "share" mixpanel + segment event if user clicks on any of the share icons.
    InspireAnalytics.track({
      eventName: InspireAnalyticEvents.SHARE,
      properties: {
        "Page Type": InspireAnalyticsHelper.getPageType(router.route),
        "Share Type": shareType,
        ...analyticsProperties,
      },
      mixpanel,
      segment,
    });
    onShare?.(shareType);
    // Also report click to Google Analytics
    googleAnalytics.event({
      category: "External Collection Share",
      action: "Select",
      label: shareType,
      value: 1,
    });
  };

  // ------------------------------------------------------------------------------------------------------------------
  //   1  LABELED BUTTONS
  // -----------------------------------------------------------------------------------------------------------------
  if (labeledButtons) {
    return (
      <>
        <style>
          @import
          url('https://fonts.googleapis.com/css2?family=Roboto:wght@900&display=swap');
        </style>
        <Group
          xs="horizontal"
          spacing={4}
          wrap={true}
          className="justify-center"
        >
          {/* Facebook Button */}
          {facebook && (
            <FacebookShareButton
              url={shareUrl}
              aria-label="share on facebook"
              onClick={() => handleClick("facebook")}
            >
              <div
                className={`flex pv-1 pl-3 justify-evenly items-center ${
                  liftOnHover && "subtleLift"
                }`}
                style={{
                  backgroundColor: "#3b5998",
                  borderRadius: "7px",
                  width: "132px",
                  height: "44px",
                }}
              >
                <p
                  className="white fwb ttu mb-0 f-3 tc usn"
                  style={{ fontFamily: "Roboto" }}
                >
                  Share On
                </p>
                <FacebookIcon size={32} />
              </div>
            </FacebookShareButton>
          )}

          {/* Twitter Button */}
          {twitter && (
            <TwitterShareButton
              url={shareUrl}
              aria-label="share on X"
              onClick={() => handleClick("twitter")}
            >
              <div
                className={`flex pv-1 pl-3 pr-3 justify-evenly items-center ${
                  liftOnHover && "subtleLift"
                }`}
                style={{
                  backgroundColor: "#000000",
                  borderRadius: "7px",
                  width: "132px",
                  height: "44px",
                }}
              >
                <p
                  className="white fwb ttu mb-0 f-3 tc usn"
                  style={{ fontFamily: "Roboto" }}
                >
                  POST
                </p>
                <XOutlined style={{ fontSize: "16px", color: "white" }} />
              </div>
            </TwitterShareButton>
          )}

          {/* Email Button
           *** If a rsvpUrl is passed in, this should launch the rsvp flow instead.
           */}

          {email && rsvpUrl ? (
            // - with rsvpUrl (launches rsvp flow)
            <Link href={rsvpUrl}>
              <div
                onClick={() => handleClick("invite")}
                className={`flex pv-1 pl-3 justify-evenly items-center pointer ${
                  liftOnHover ? "subtleLift" : ""
                }`}
                style={{
                  backgroundColor: "#7f7f7f",
                  borderRadius: "7px",
                  width: "132px",
                  height: "44px",
                }}
              >
                <p
                  className="white fwb ttu mb-0 f-3 tc usn"
                  style={{ fontFamily: "Roboto" }}
                >
                  INVITE
                </p>
                <EmailIcon size={32} />
              </div>
            </Link>
          ) : (
            // - without rsvpUrl (default)
            <EmailShareButton
              subject={shareMessageSubject}
              body={shareMessageBody || defaultShareBody}
              url={shareUrl}
              aria-label="share through email"
            >
              <div
                onClick={() => handleClick("email")}
                className={`flex pv-1 pl-3 justify-evenly items-center ${
                  liftOnHover && "subtleLift"
                }`}
                style={{
                  backgroundColor: "#7f7f7f",
                  borderRadius: "7px",
                  width: "132px",
                  height: "44px",
                }}
              >
                <p
                  className="white fwb ttu mb-0 f-3 tc usn"
                  style={{ fontFamily: "Roboto" }}
                >
                  EMAIL
                </p>
                <EmailIcon size={32} />
              </div>
            </EmailShareButton>
          )}

          {/* Clipboard Button */}
          {clipboard && (
            <div
              onClick={() => handleClick("clipboard")}
              className={`flex pv-1 pl-3 justify-evenly items-center ${
                liftOnHover && "subtleLift"
              }`}
              style={{
                backgroundColor: "gray",
                borderRadius: "7px",
                width: "132px",
                height: "44px",
              }}
            >
              <p
                className="white fwb ttu mb-0 f-3 tc usn"
                style={{ fontFamily: "Roboto" }}
              >
                COPY
              </p>
              <CopyToClipboard
                text={shareUrl}
                className="w-8"
                style={{
                  backgroundColor: "gray",
                }}
                iconClassName="white"
                iconStyle={{ fontSize: "32px" }}
                aria-label="copy url"
              />
            </div>
          )}
        </Group>
      </>
    );
  }

  // ------------------------------------------------------------------------------------------------------------------
  //   2  NON-LABELED BUTTONS (DEFAULT)
  // ------------------------------------------------------------------------------------------------------------------
  return (
    <Group xs="horizontal" spacing={4} className="justify-center items-center">
      {/* Facebook Button */}
      {facebook && (
        <FacebookShareButton
          url={shareUrl}
          aria-label="share on facebook"
          onClick={() => handleClick("facebook")}
        >
          <div
            className={`${buttonClass} ${
              liftOnHover && "subtleLift"
            } flex items-center justify-center`}
            style={{
              backgroundColor: "#3b5998",
              borderRadius: rectangularButtons ? "0" : "50%",
              overflow: "hidden",
            }}
          >
            <FacebookIcon
              size={28}
              round={false}
              bgStyle={{ fill: "transparent" }}
              iconFillColor="white"
            />
          </div>
        </FacebookShareButton>
      )}

      {/* X/Twitter Button */}
      {twitter && (
        <TwitterShareButton
          url={shareUrl}
          aria-label="share on X"
          onClick={() => handleClick("twitter")}
        >
          <div
            className={`${buttonClass} ${
              liftOnHover && "subtleLift"
            } flex items-center justify-center`}
            style={{
              backgroundColor: "#000000",
              borderRadius: rectangularButtons ? "0" : "50%",
              overflow: "hidden",
            }}
          >
            <XOutlined style={{ fontSize: "18px", color: "white" }} />
          </div>
        </TwitterShareButton>
      )}

      {/* Email Button
       *** If a rsvpUrl is passed in, this should launch the rsvp flow instead. */}
      {email && rsvpUrl ? (
        <div onClick={() => handleClick("invite")}>
          <Link href={rsvpUrl}>
            <div
              className={`${buttonClass} ${
                liftOnHover && "subtleLift"
              } flex items-center justify-center pointer`}
              style={{
                backgroundColor: "#7f7f7f",
                borderRadius: rectangularButtons ? "0" : "50%",
                overflow: "hidden",
              }}
            >
              <EmailIcon
                round={false}
                bgStyle={{ fill: "transparent" }}
                iconFillColor="white"
              />
            </div>
          </Link>
        </div>
      ) : (
        <EmailShareButton
          onClick={() => handleClick("email")}
          subject={shareMessageSubject}
          body={shareMessageBody || defaultShareBody}
          url={shareUrl}
          aria-label="share through email"
          className={`${buttonClass} ${
            liftOnHover && "subtleLift"
          } flex items-center justify-center`}
          style={{
            backgroundColor: "#7f7f7f",
            borderRadius: rectangularButtons ? "0" : "50%",
            overflow: "hidden",
          }}
        >
          <EmailIcon
            className={`${buttonClass} ${liftOnHover && "subtleLift"}`}
          />
        </EmailShareButton>
      )}

      {/* Clipboard Button */}
      {clipboard && (
        <div
          className={`${buttonClass} ${
            liftOnHover && "subtleLift"
          } flex items-center justify-center`}
          style={{
            backgroundColor: "gray",
            borderRadius: rectangularButtons ? "0" : "50%",
            overflow: "hidden",
          }}
        >
          <CopyToClipboard
            text={shareUrl}
            className="flex items-center justify-center"
            style={{
              backgroundColor: "transparent",
              border: "none",
              padding: 0,
              width: "100%",
              height: "100%",
            }}
            round={false}
            iconClassName="white"
            iconStyle={{ fontSize: "18px" }}
            aria-label="copy url"
            onClick={() => handleClick("clipboard")}
          />
        </div>
      )}
    </Group>
  );
};

export default ShareBar;
